import React, {useEffect, useState} from 'react';
import {HStack, useTheme, VStack, Box, Flex, AvatarGroup} from '@chakra-ui/react';
import {TextG10, TextG12, TextG14} from '../../typography';
import {generatePath, Link, useNavigate, useParams} from 'react-router-dom';
import {useTrackVisibility} from 'react-intersection-observer-hook';
import useVisibleArticles from '../../../hooks/useVisible';
import {storeScrollPositionInSession} from '../../../utils/scroll';
import NonPremiumCard from '../../Card/NonPremiumCard';
import DefaultEventImage from '../../../assets/images/default_event.png';
import Icon from '../../Icon';
import {getCalenderDate} from '../../../utils/date';
import {BlueButton} from '../../button';
import {ROUTES} from '../../../constants/routes';
import {Event} from '../../../types/article';
import JoinMeetUpModal from '../../JoinMeetUpModal';
import {OPTIONS} from '../../../constants/meetupOptions';
import {EventRSVPStatus, useFetchEventRSVPStats, useFetchEventInterestedGoingUsers, useSetAttendance} from '../../../hooks/useEvent';
import RSVPSelect from '../../../pages/Event/RSVP/RSVPSelect';
import RSVPRestrictionModal from '../../../pages/Event/RSVP/RSVPRestrictionModal';
import useCountdown from '../../../hooks/useCountdown';
import useAuth from '../../../hooks/useAuth';
import {UsersListModal} from '../../UsersList';
import UserAvatar from '../../UserAvatar';

// Ambassador topic ID - when this topic is present, show the "Meet a Community Connector" badge
const AMBASSADOR_PRESENT_TOPIC = process.env.NODE_ENV === 'production' ? 336 : 223;

const MeetUpCard = ({
  'meet-up': event,
  leftTopItem,
  rightTopItem,
  modal,
  isSlide,
  onClick,
}: {
  'meet-up'?: Event;
  leftTopItem?: any;
  rightTopItem?: any;
  modal?: boolean;
  isSlide?: boolean;
  onClick?: (e) => void;
}) => {
  if (!event) return null;
  const theme = useTheme();
  const navigate = useNavigate();
  const {pathname} = useParams();
  const [ref, {wasEverVisible, isVisible}] = useTrackVisibility();
  const {addVisibleArticle} = useVisibleArticles();
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [isSelectOpen, setSelectIsOpen] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [isRestrictionModalOpen, setIsRestrictionModalOpen] = useState(false);
  const {mutateAsync} = useSetAttendance();
  const {endPassed} = useCountdown(event);
  const {user} = useAuth();

  const {data} = useFetchEventRSVPStats(event.id, !onClick && isVisible && !!user);
  const rsvp_stats = data?.data.rsvp_stats;
  const usersGoing = rsvp_stats?.GOING;
  const {data: interestedData} = useFetchEventInterestedGoingUsers(event.id);
  const totalInterestedGoing = interestedData?.pages[0]?.data?.total || 0;
  const interestedUsers = interestedData?.pages[0]?.data?.data || [];

  const statusValue = event.rsvp_status?.status_value;
  const noStatus = !statusValue || statusValue == 0;
  const currentOption = statusValue ? OPTIONS[statusValue] : null;
  const rsvpText = statusValue && currentOption
    ? endPassed
      ? currentOption.disabledText || ''
      : currentOption.text || ''
    : '';

  const isHost = user?.id == (event?.user?.id || event.user_id);

  // Check if user can RSVP to this event
  const canRsvp = event?.can_rsvp !== false;

  const renderIcon =
    statusValue && currentOption && currentOption.renderSelectedIcon
      ? currentOption.renderSelectedIcon
      : null;
  const renderIconFunc =
    statusValue && currentOption && currentOption.color && renderIcon
      ? renderIcon({
          style: {
            fontSize: '18px',
            height: '18px',
            width: '18px',
            color:
              theme.colors[currentOption.color || 'white'] || currentOption.color,
            fill: theme.colors[currentOption.color || 'blue'] || currentOption.color,
          },
        })
      : null;

  const onClickRSVPButton = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If user cannot RSVP due to membership restrictions, show upgrade modal
    if (!canRsvp && event?.rsvp_restriction_reason === 'local_insiders_membership_required') {
      setIsRestrictionModalOpen(true);
      return;
    }
    
    if (noStatus) {
      setShowJoinModal(true);
    } else {
      setSelectIsOpen(true);
    }
  };
  
  const onUpgradeClick = () => {
    setIsRestrictionModalOpen(false);
    navigate('/memberships');
  };

  const onClickRSVPModal = (status: EventRSVPStatus) => {
    setSelectIsOpen(false);
    mutateAsync({
      eventId: event?.id,
      prevStatus: event?.rsvp_status?.status_value || EventRSVPStatus.NO_STATUS,
      status,
    });
  };

  useEffect(() => {
    if (wasEverVisible && event && event?.id?.toString()) {
      addVisibleArticle({id: event?.id?.toString(), type: 'event'});
    }
  }, [wasEverVisible]);

  const iconStyle: React.CSSProperties = React.useMemo(
    () => ({
      color: '#374957',
      fontSize: '14px',
    }),
    [theme]
  );

  // Check if this meetup has the ambassador topic
  const hasAmbassadorTopic = event?.topics_of_interest?.some(
    (topic) => topic.id === AMBASSADOR_PRESENT_TOPIC
  );

  return (
    <>
      <Box position="relative" width="100%" pt={hasAmbassadorTopic ? '32px' : 0}>
        {hasAmbassadorTopic && (
          <Box
            position="absolute"
            top="-4px"
            left="16px"
            backgroundColor="#FAE7F0"
            borderRadius="8px 8px 0 0"
            px="8px"
            py="6px"
            zIndex={10}>
            <TextG12 fontWeight="500" color="#C9136B" lineHeight="18px">
              👋 Meet a Community Connector
            </TextG12>
          </Box>
        )}
        <NonPremiumCard
          onClick={(e) => {
            if (onClick) {
              onClick(e);
              return;
            }
            if (
              e?.target.tagName === 'BUTTON' ||
              e?.target.parentNode.tagName === 'BUTTON' ||
              e?.target.parentNode.parentNode.tagName === 'BUTTON'
            )
              return;

            const route = modal
              ? {pathname, search: `?id=${event.id.toString()}&articleType=meet-up`}
              : generatePath(ROUTES.MEET_UP, {
                  meetupId: event.id.toString(),
                });
            storeScrollPositionInSession();
            navigate(route);
          }}
          isSlide={isSlide}
          height={'auto'}
          coverImage={(event.place?.image_url || event.event?.image_url || event.image_url || DefaultEventImage) as string}
          leftTopItem={leftTopItem}
          rightTopItem={rightTopItem}
          childPaddingX={4}
          article={event}
          minHeight="100%"
          maxHeight="100%"
          outline={hasAmbassadorTopic ? '2px solid #E86EAA' : undefined}>
          <VStack ref={ref} align="start" pr={8} gap="5px">
            <TextG10 color={theme.colors.grey}>
              {getCalenderDate(event?.start_date_time)?.toUpperCase()}
            </TextG10>
            <TextG14
              mt="0px !important"
              isTruncated="&hellip;"
              noOfLines={null}
              fontWeight={500}
              color={theme.colors.lightBlack}>
              {event?.title}
            </TextG14>
            {totalInterestedGoing > 0 && (
              <Flex
                align="center"
                gap={2}
                mt="5px !important"
                alignSelf="flex-start"
                cursor="pointer"
                _hover={{opacity: 0.8}}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowUsersModal(true);
                }}>
                <AvatarGroup size="xs" max={3} spacing={-2}>
                  {interestedUsers.slice(0, 3).map((user: any, index: number) => (
                    <UserAvatar
                      key={user.user_id || `user-${index}`}
                      user={{
                        id: user.user_id,
                        name: user.name,
                        surname: user.surname,
                        handle: user.handle,
                        profilePhotoUrl: user.profile_photo_url,
                      } as any}
                      size="xs"
                      avatar_image={user.profile_photo_url || undefined}
                      sx={{
                        border: '1.5px solid white',
                        width: '20px',
                        height: '20px',
                      }}
                    />
                  ))}
                </AvatarGroup>
                <TextG10
                  color={theme.colors.blue}
                  _hover={{textDecoration: 'underline'}}>
                  {`${totalInterestedGoing} Neighbor${totalInterestedGoing !== 1 ? 's' : ''} Interested`}
                </TextG10>
              </Flex>
            )}

            {event?.place && (
              <HStack maxW="110px">
                <Icon iconName="fi-rr-marker" style={iconStyle} />
                <TextG10 ml="4px !important" color={theme.colors.grey}>
                  {event.place.name}
                </TextG10>
              </HStack>
            )}
            {!isHost && !endPassed && (
              <Box pos="absolute" bottom="10px" right="5px">
                <BlueButton
                  p={2}
                  color={noStatus ? 'white' : 'black'}
                  backgroundColor={noStatus ? theme.colors.blue : currentOption?.bgColor || 'transparent'}
                  onClick={onClickRSVPButton}>
                  <Flex
                    direction={'row'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    gap={'.5rem'}>
                    {renderIconFunc}
                    <TextG12 fontWeight="500">{noStatus ? `✋ Count me in!` : rsvpText}</TextG12>
                  </Flex>
                </BlueButton>
              </Box>
            )}
          </VStack>
        </NonPremiumCard>
      </Box>
      <JoinMeetUpModal event={event} show={showJoinModal} setShow={setShowJoinModal} />
      <RSVPSelect
        rsvpOptions={OPTIONS}
        onOptionClick={onClickRSVPModal}
        onModalClose={() => setSelectIsOpen(false)}
        openModal={isSelectOpen}
        event={event}
      />
      <UsersListModal
        isOpen={showUsersModal}
        onClose={() => setShowUsersModal(false)}
        title="Neighbors Interested"
        totalCount={totalInterestedGoing}
        fetchUsersHook={useFetchEventInterestedGoingUsers}
        entityId={event.id}
        chatRoute={`/meet-up/${event.id}/chat`}
      />
      <RSVPRestrictionModal
        isOpen={isRestrictionModalOpen}
        onClose={() => setIsRestrictionModalOpen(false)}
        onUpgradeClick={onUpgradeClick}
      />
    </>
  );
};

export default MeetUpCard;
